import React, { useState } from 'react';
import type { Site } from '@/types';
import { ic } from '@/components/Icons';
import { DataTable, PageHeader, Pagination, ProgressBar, type Column } from '@/components/ui';
import { HeaderTip } from '@/components/ui/HeaderTip';
import { HoverCard, HoverRow } from '@/components/ui/HoverCard';
import { COUNTRIES, ds, userOrgIds } from '@/data';
import { useI18n } from '@/i18n';
import { completeness, siteSchema } from '@/lib/fields';
import { siteHasRed } from '@/hooks/useScopedData';
import { paginate, sortRows, useTableState, type SortMap } from '@/hooks/useTableState';
import { useGo } from '@/hooks/useRoute';
import { useAppStore } from '@/store/useAppStore';
import { esDeUnSite } from '@/lib/roles';

type SiteRow = Site & { _key: string; _pct: number; _missing: number; _primary: boolean };

/** Compact summary of the most relevant site data, on hover. */
function SiteNameCell({ r }: { r: SiteRow }) {
  const { lang, t } = useI18n();
  const country = COUNTRIES.find((c) => c.code === r.SiteCountry)?.[lang === 'es' ? 'name_es' : 'name_en'] || r.SiteCountry;
  const addr = [r.SiteAddress1, r.SiteCity, r.SiteStateProvince].filter(Boolean).join(', ');
  const cpBad = !r.SitePostalCode || r.SitePostalCode === '0000';
  const wsBad = !r.Workstations;

  return (
    <HoverCard
      width={320}
      height={280}
      trigger={
        <>
          <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            {r.SiteName}
            {r._primary ? (
              <span data-tip={t('sites.primary_label')} style={{ color: 'var(--ok)', display: 'inline-flex', flexShrink: 0, cursor: 'help' }}>
                {React.cloneElement(ic.check, { size: 15, stroke: 2.5 })}
              </span>
            ) : null}
          </div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.SiteId} · ATCCSN {r.ATCCSN}</div>
        </>
      }
    >
      {() => (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--brand-ink)', letterSpacing: '-0.01em' }}>{r.SiteName}</div>
              <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 2 }}>{r.SiteId} · ATCCSN {r.ATCCSN}</div>
            </div>
            {r._primary ? <span className="chip brand" style={{ fontSize: 9, flexShrink: 0 }}>{t('sites.primary_label')}</span> : null}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            <HoverRow label={lang === 'es' ? 'Organización' : 'Organization'}>{r.OrgName}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Dirección' : 'Address'}>{addr || '—'}</HoverRow>
            <HoverRow label={lang === 'es' ? 'País' : 'Country'}>{country}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Código postal' : 'Postal code'} bad={cpBad}>
              {!r.SitePostalCode
                ? lang === 'es' ? 'Sin dato' : 'Missing'
                : r.SitePostalCode === '0000'
                  ? lang === 'es' ? '0000 — inválido' : '0000 — invalid'
                  : r.SitePostalCode}
            </HoverRow>
            <HoverRow label={lang === 'es' ? 'Estaciones' : 'Workstations'} bad={wsBad}>
              {r.Workstations ? `${r.Workstations} ${lang === 'es' ? 'equipos' : 'units'}` : lang === 'es' ? 'Sin dato' : 'Missing'}
            </HoverRow>
            {r.SiteTelephone ? <HoverRow label={lang === 'es' ? 'Teléfono' : 'Phone'}>{r.SiteTelephone}</HoverRow> : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, flexShrink: 0 }}>{t('common.completeness')}</span>
            <div style={{ flex: 1 }}><ProgressBar pct={r._pct} /></div>
            <span className="mono" style={{ fontSize: 12, fontWeight: 700 }}>{r._pct}%</span>
          </div>
        </>
      )}
    </HoverCard>
  );
}

export function SitesList() {
  const { lang, t } = useI18n();
  const go = useGo();
  const role = useAppStore((s) => s.role);
  const user = useAppStore((s) => s.user);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'all' | 'red' | 'primary'>('all');
  const table = useTableState(15);

  let all = ds.listSites({ q });
  if (role === 'PARTNER_MGR') {
    const oids = userOrgIds(user);
    all = all.filter((s) => oids.includes(s.OrgId));
  } else if (esDeUnSite(role) && user?.siteId) {
    all = all.filter((s) => s.SiteId === user.siteId);
  }
  if (filter === 'red') all = all.filter(siteHasRed);
  if (filter === 'primary') all = all.filter((s) => s.SiteId === s.PrimarySiteId);

  // enrich every match first, so completeness + sorting span all pages
  const enriched: SiteRow[] = all.map((s) => {
    const c = completeness(siteSchema, s);
    return { ...s, _key: s.SiteId, _pct: c.pct, _missing: c.missing, _primary: s.SiteId === s.PrimarySiteId };
  });

  const sortValues: SortMap<SiteRow> = {
    p: { type: 'number', get: (r) => (r._primary ? 1 : 0) },
    SiteName: { type: 'text', get: (r) => r.SiteName },
    OrgName: { type: 'text', get: (r) => r.OrgName },
    SiteCity: { type: 'text', get: (r) => r.SiteCity },
    cp: { type: 'text', get: (r) => r.SitePostalCode || '' },
    ws: { type: 'number', get: (r) => Number(r.Workstations) || 0 },
    pct: { type: 'number', get: (r) => r._pct },
  };

  const { rows, page, pageCount } = paginate(sortRows(enriched, table.sort, sortValues), table.page, table.per);

  const columns: Column<SiteRow>[] = [
    {
      key: 'p',
      label: '',
      sortable: true,
      render: (r) =>
        r._primary ? (
          <span data-tip={t('sites.primary_tip')} style={{ color: '#FFB020' }}>
            {React.cloneElement(ic.star, { size: 14, fill: 'currentColor' })}
          </span>
        ) : (
          ''
        ),
    },
    { key: 'SiteName', label: 'Site', sortable: true, render: (r) => <SiteNameCell r={r} /> },
    {
      key: 'OrgName',
      label: lang === 'es' ? 'Organización' : 'Organization',
      muted: true,
      sortable: true,
      render: (r) => (
        <div>
          <div>{r.OrgName}</div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.OrgId}</div>
        </div>
      ),
    },
    { key: 'SiteCity', label: lang === 'es' ? 'Ciudad' : 'City', sortable: true },
    {
      key: 'cp',
      label: <HeaderTip label="CP" tip={lang === 'es' ? 'Código postal' : 'Postal code'} />,
      sortable: true,
      render: (r) =>
        !r.SitePostalCode ? <span className="chip err">—</span>
        : r.SitePostalCode === '0000' ? <span className="chip err mono">{r.SitePostalCode}</span>
        : <span className="mono">{r.SitePostalCode}</span>,
    },
    {
      key: 'ws',
      label: <HeaderTip label="WS" tip={lang === 'es' ? 'Estaciones de trabajo (equipos)' : 'Workstations'} />,
      align: 'right',
      sortable: true,
      render: (r) => (
        <span className="mono" style={{ color: r.Workstations ? 'var(--text)' : 'var(--err)', fontWeight: 600 }}>
          {r.Workstations || '!'}
        </span>
      ),
    },
    {
      key: 'pct',
      label: t('common.completeness'),
      sortable: true,
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
          <div style={{ flex: 1 }}><ProgressBar pct={r._pct} /></div>
          <span className="mono" style={{ fontSize: 11, fontWeight: 700, minWidth: 30, textAlign: 'right', color: r._pct === 100 ? 'var(--ok)' : 'var(--text-2)' }}>
            {r._pct}%
          </span>
        </div>
      ),
    },
    { key: 'go', label: '', align: 'right', render: () => <span style={{ color: 'var(--text-3)' }}>{ic.chevR}</span> },
  ];

  const filters = [
    { k: 'all' as const, l: t('common.all') },
    { k: 'red' as const, l: lang === 'es' ? 'Con rojos' : 'Reds pending' },
    { k: 'primary' as const, l: t('sites.primary_label') },
  ];

  return (
    <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PageHeader
        title={t('nav.sites')}
        sub={lang === 'es' ? `${all.length} sites en tu alcance` : `${all.length} sites in scope`}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', background: 'var(--bg)', borderRadius: 7, padding: 2, border: '1px solid var(--line)' }}>
              {filters.map((f) => (
                <button
                  key={f.k}
                  className="btn sm"
                  onClick={() => {
                    setFilter(f.k);
                    table.setPage(1);
                  }}
                  aria-pressed={filter === f.k}
                  style={{
                    background: filter === f.k ? 'var(--surface)' : 'transparent',
                    border: 'none',
                    boxShadow: filter === f.k ? 'var(--shadow-1)' : 'none',
                    color: filter === f.k ? 'var(--brand-ink)' : 'var(--text-2)',
                    fontWeight: 600, fontSize: 12,
                  }}
                >
                  {f.l}
                </button>
              ))}
            </div>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}>{ic.search}</span>
              <input
                placeholder={t('common.search')}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  table.setPage(1);
                }}
                style={{ padding: '8px 12px 8px 32px', border: '1px solid var(--line-2)', borderRadius: 7, fontSize: 13, width: 220 }}
              />
            </div>
          </div>
        }
      />

      <DataTable columns={columns} rows={rows} sort={table.sort} onSort={table.onSort} stickyHeader onRowClick={(s) => go('site', { siteId: s.SiteId })} />
      <Pagination page={page} pageCount={pageCount} onPage={table.setPage} total={all.length} per={table.per} onPer={table.onPer} />
    </div>
  );
}
